// src/pages/MyMonthlyOverview.js
import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
} from "react-bootstrap";
import Chart from "react-apexcharts";
import Cookies from "js-cookie";
import api from "../../api.js";
import moment from "jalali-moment";

export default function MyMonthlyOverview() {
  const token = Cookies.get("access_token");
  const username = Cookies.get("username");

  const today = moment();
  const [selectedMonth, setSelectedMonth] = useState(today.jMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(today.jYear());
  const [monthlyOverview, setMonthlyOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");

  const jalaliYears = Array.from({ length: 6 }, (_, i) => today.jYear() - i);

  const presenceLabels = {
    "on-site": "حضوری",
    "remote": "دورکار",
    "": "-",
  };

  const toPersianNumber = (num) => {
    const persianDigits = ["۰","۱","۲","۳","۴","۵","۶","۷","۸","۹"];
    return num.toString().replace(/\d/g, (d) => persianDigits[d]);
  };

  const toHM = (val) => {
    const v = Number(val || 0);
    const h = Math.floor(v / 60);
    const m = Math.floor(v % 60);
    return `${h}س ${m}د`;
  };

  const fetchOverview = async () => {
    setLoading(true);
    setMsg("");
    try {
      await api.post('/monthly-overview/update/');
      const res = await api.get(`/users/me/overview/${selectedYear}/${selectedMonth}/`, {
  headers: { Authorization: `Bearer ${token}` },
});

      setMonthlyOverview(res.data || null);
    } catch (err) {
      console.error(err);
      setMsg("❌ خطا در دریافت گزارش ماهانه");
      setMonthlyOverview(null);
    }
    setLoading(false);
  };

  const { pieOptions, pieSeries, barOptions, barSeries } = useMemo(() => {
    if (!monthlyOverview) return { pieOptions: {}, pieSeries: [], barOptions: {}, barSeries: [] };

    const safe = (k) => Number(monthlyOverview[k] || 0);
    const otherTitles = monthlyOverview.total_other_titles || {};

    const pieLabels = [
      "گزارش فناوری",
      "امتیازدهی فناوری",
      "گزارش تجمیعی",
      "بارگزاری در سایت",
      ...Object.keys(otherTitles),
    ];
    const pieSeriesArr = [
      safe("total_report_minutes"),
      safe("total_scoring_minutes"),
      safe("total_aggregate_minutes"),
      safe("total_upload_minutes"),
      ...Object.values(otherTitles).map((v) => Number(v || 0)),
    ];

    const barLabels = ["گزارش فناوری", "امتیازدهی فناوری", "بارگزاری در سایت"];
    const barSeriesArr = [
      {
        name: "تعداد گزارش‌ها",
        data: [
          Number(monthlyOverview.total_report_count || 0),
          Number(monthlyOverview.total_scoring_report_count || 0),
          Number(monthlyOverview.total_user_reports || 0),
        ],
      },
    ];

    const pieOpts = {
      chart: { type: "pie", foreColor: "#000", toolbar: { show: false }, parentHeightOffset: 0 },
      labels: pieLabels,
      legend: { position: "bottom" },
      tooltip: {
        y: {
          formatter: (val) => {
            const h = Math.floor(val / 60);
            const m = Math.floor(val % 60);
            return `${toPersianNumber(h)}س ${toPersianNumber(m)}د`;
          },
        },
        style: { fontFamily: "IranSans" },
      },
      dataLabels: {
        enabled: true,
        formatter: (val) => toPersianNumber(val.toFixed(1)) + "%",
        style: { fontSize: "12px", fontFamily: "IranSans" },
      },
      responsive: [
        { breakpoint: 768, options: { chart: { width: "100%" }, legend: { position: "bottom" } } },
      ],
    };

    const barOpts = {
      chart: { toolbar: { show: true } },
      plotOptions: { bar: { horizontal: false, columnWidth: "45%" } },
      dataLabels: { enabled: false },
      xaxis: { categories: barLabels },
      yaxis: { labels: { formatter: (val) => toPersianNumber(val) } },
      tooltip: { y: { formatter: (val) => toPersianNumber(val) } },
    };

    return { pieOptions: pieOpts, pieSeries: pieSeriesArr, barOptions: barOpts, barSeries: barSeriesArr };
  }, [monthlyOverview]);

  return (
    <div className="container mt-3">

      {msg && <Alert variant="danger">{msg}</Alert>}

      <Form className="mb-3 d-flex flex-wrap gap-2 align-items-end">
        <Form.Group style={{ minWidth: 120 }}>
          <Form.Label>ماه</Form.Label>
          <Form.Select value={selectedMonth} onChange={(e) => setSelectedMonth(Number(e.target.value))}>
            {[...Array(12)].map((_, idx) => (
              <option key={idx + 1} value={idx + 1}>{toPersianNumber(idx + 1)}</option>
            ))}
          </Form.Select>
        </Form.Group>

        <Form.Group style={{ minWidth: 140 }}>
          <Form.Label>سال</Form.Label>
          <Form.Select value={selectedYear} onChange={(e) => setSelectedYear(Number(e.target.value))}>
            {jalaliYears.map((y) => (
              <option key={y} value={y}>{toPersianNumber(y)}</option>
            ))}
          </Form.Select>
        </Form.Group>

        <Button variant="btn-submit" onClick={fetchOverview}>مشاهده</Button>
      </Form>

      {loading && <div className="text-center py-4"><Spinner animation="border" /></div>}

      {monthlyOverview ? (
        <>
          <Row className="g-3 mb-3">
            <Col md={4} sm={12}>
              <Card className="p-3 h-100">
                <h6 className="mb-2">فعالیت‌ها</h6>
                <div>گزارش فناوری: {toPersianNumber(toHM(monthlyOverview.total_report_minutes))}</div>
                <div>امتیازدهی فناوری: {toPersianNumber(toHM(monthlyOverview.total_scoring_minutes))}</div>
                <div>بارگذاری در سایت: {toPersianNumber(toHM(monthlyOverview.total_upload_minutes))}</div>
                <div>گزارش تجمیعی: {toPersianNumber(toHM(monthlyOverview.total_aggregate_minutes))}</div>

                {monthlyOverview.total_other_titles && Object.entries(monthlyOverview.total_other_titles).map(([title, mins]) => (
                  <div key={title} className="small text-secondary">• {title}: {toPersianNumber(toHM(mins))}</div>
                ))}
              </Card>
            </Col>

            <Col md={4} sm={12}>
              <Card className="p-3 h-100">
                <h6 className="mb-2">ساعات کاری</h6>
                <div style={{ fontSize: 18, fontWeight: 700 }}>
                  {toPersianNumber(monthlyOverview.total_work_hm?.[0] ?? 0)} ساعت و {toPersianNumber(monthlyOverview.total_work_hm?.[1] ?? 0)} دقیقه
                </div>
                <div className="text-muted small mt-1">مجموع دقیقه: {toPersianNumber(monthlyOverview.total_work_minutes ?? 0)}</div>
                <div className="text-muted small mt-1">
                  متوسط زمان گزارش فناوری: {toPersianNumber(monthlyOverview.report_ratio_hm?.[0])}س {toPersianNumber(monthlyOverview.report_ratio_hm?.[1])}د
                </div>
                <div className="text-muted small mt-1">
                  متوسط زمان امتیازدهی فناوری: {toPersianNumber(monthlyOverview.scoring_ratio_hm?.[0])}س {toPersianNumber(monthlyOverview.scoring_ratio_hm?.[1])}د
                </div>
                <div className="text-muted small mt-1">
  متوسط زمان بارگزاری در سایت:             {toPersianNumber(monthlyOverview.upload_ratio_hm?.[0])}س {toPersianNumber(monthlyOverview.upload_ratio_hm?.[1])}د
              </div>
                <div className="text-muted small mt-1">* زمان به ازای هر گزارش</div>
              </Card>
            </Col>

            <Col md={4} sm={12}>
              <Card className="p-3 h-100">
                <h6 className="mb-2">حضور</h6>
                <div>روزهای معادل حضوری: {toPersianNumber(Math.round(monthlyOverview.days_on_site * 10) / 10)}</div>
                <div className="text-muted small mt-1">نوع حضور غالب: {presenceLabels[monthlyOverview.most_presence_type] || "-"}</div>
                <div className="mt-2 small text-secondary">
                  شمار گزارش های بارگزاری شده: {toPersianNumber(monthlyOverview.total_user_reports ?? 0)}
                </div>
                <div className="mt-2 small text-secondary">
                      شمار گزارش های امتیازدهی شده: {toPersianNumber(monthlyOverview.total_scoring_report_count) ?? 0}
                    </div>
                    <div className="mt-2 small text-secondary">
                      شمار گزارش های فناوری: {toPersianNumber(monthlyOverview.total_report_count) ?? 0}
                    </div>
              </Card>
            </Col>
          </Row>

          <Row className="g-3">
            <Col lg={6} md={12} sm={12}>
              <Card className="p-3 h-100">
                <h6 className="mb-2">تقسیم‌بندی ساعات فعالیت‌ها</h6>
                <Chart type="pie" height={320} series={pieSeries} options={pieOptions} />
                <div className="text-muted small mt-2">
                  مقدارها بر حسب دقیقه هستند، نشانگر را نگه دارید تا ساعت:دقیقه نمایش داده شود.
                </div>
              </Card>
            </Col>

          </Row>
          <Row className="g-3">
            

            <Col lg={6} md={12} sm={12}>
              <Card className="p-3 h-100">
                <h6 className="mb-2">تعداد گزارش‌ها (مقایسه)</h6>
                <Chart type="bar" height={320} series={barSeries} options={barOptions} />
                <div className="text-muted small mt-2">ستون‌ها تعداد گزارش‌ها را نشان می‌دهند.</div>
              </Card>
            </Col>
          </Row>
        </>
      ) : !loading ? (
        <div className="text-center py-4 text-muted small">
          برای این ماه گزارشی یافت نشد. روی "مشاهده" کلیک کنید تا محاسبه و بارگذاری شود.
        </div>
      ) : null}
    </div>
  );
}
